import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import {
  CORE_INVITATION_FIELDS,
  CORE_MEMBER_FIELDS,
} from './graphql/fragments';
import {
  deleteInvitations,
  resendInvitations,
  sendInvitations,
} from './graphql/mutations';
import { listInvitations } from './graphql/queries';

export default function useInvitations({
  useGraphQLMutation,
  useLazyGraphQLQuery,
  isManager,
  organizationId,
  setFormStatus,
}) {
  const { t } = useTranslation();

  const [listInvitationsQuery, listInvitationsResponse] =
    useLazyGraphQLQuery(listInvitations);

  const limit = 1000;

  const [invitationsFormStatus, setInvitationsFormStatus] = useState();
  const [inviteMembersOpen, setInviteMembersOpen] = useState(false);
  const [showMultipleFeedback, setShowMultipleFeedback] = useState(false);

  const [failureList, setFailureList] = useState([]);
  const [successList, setSuccessList] = useState([]);

  const onCloseInviteMembers = () => {
    setInviteMembersOpen(false);
    setFormStatus(null);
  };

  const handleCompletedInvitationMutation = (data) => {
    const successMembersParsed = data.successMembers.map((member) => ({
      email: member.user.email,
      name: member.user.name,
      role: member.role,
    }));
    setFailureList(data.failed);
    setSuccessList([
      ...data.successInvitations,
      ...successMembersParsed,
      ...(data.emailSentInvitations ?? []),
    ]);
    setShowMultipleFeedback(true);
  };
  const handleErrorInvitationMutation = (error) => {
    setInvitationsFormStatus({ success: false, message: `${error.message}` });
  };

  const [deleteInvitationsMutation] = useGraphQLMutation(deleteInvitations, {
    onCompleted: ({ deleteInvitations: data }) =>
      handleCompletedInvitationMutation(data),
    onError: handleErrorInvitationMutation,
    update(cache, { data: { deleteInvitations } }) {
      deleteInvitations.successInvitations.forEach((deleted) => {
        cache.evict({ id: cache.identify(deleted) });
      });
      cache.gc();
    },
  });

  const [resendInvitationsMutation] = useGraphQLMutation(resendInvitations, {
    onCompleted: ({ resendInvitations: data }) =>
      handleCompletedInvitationMutation(data),
    onError: handleErrorInvitationMutation,
  });

  const [sendInvitationsMutation] = useGraphQLMutation(sendInvitations, {
    onCompleted: ({ sendInvitations: data }) => {
      handleCompletedInvitationMutation(data);
      setInviteMembersOpen(false);
    },
    onError: () => {
      setFormStatus({
        success: false,
        message: t('administrateUsers.inviteFailure'),
      });
    },
    update(cache, { data: { sendInvitations } }) {
      cache.modify({
        fields: {
          listInvitations(existingInvitations = []) {
            const newInvitations = sendInvitations.successInvitations.map(
              (invitation) => {
                return cache.writeFragment({
                  data: invitation,
                  fragment: CORE_INVITATION_FIELDS,
                  fragmentName: 'CoreInvitationFields',
                });
              }
            );
            return [...existingInvitations, ...newInvitations];
          },
          listMembers(existingMembers = []) {
            const newMembers = sendInvitations.successMembers.map((member) => {
              return cache.writeFragment({
                data: member,
                fragment: CORE_MEMBER_FIELDS,
                fragmentName: 'CoreMemberFields',
              });
            });
            return [...existingMembers, ...newMembers];
          },
        },
      });
    },
  });

  const deleteInvitationsHandler = (users) => {
    deleteInvitationsMutation({
      variables: {
        input: {
          organizationId,
          users,
        },
      },
    });
  };

  const resendInvitationsHandler = (users) => {
    resendInvitationsMutation({
      variables: {
        input: {
          organizationId,
          users,
        },
      },
    });
  };

  const sendInvitationsHandler = (users) => {
    sendInvitationsMutation({
      variables: {
        input: {
          organizationId,
          users,
        },
      },
    });
  };

  useEffect(() => {
    const { data, loading } = listInvitationsResponse;

    if (!loading && data) {
      if (
        data.listInvitations.length > 0 &&
        data.listInvitations.length % limit === 0
      ) {
        listInvitationsResponse.fetchMore({
          variables: {
            pagination: {
              offset: data.listInvitations.length,
              limit,
            },
          },
        });
      }
    }
  }, [listInvitationsResponse.data, listInvitationsResponse.loading]);

  useEffect(() => {
    if (!isManager && organizationId) {
      listInvitationsQuery({
        variables: {
          pagination: {
            offset: 0,
            limit,
          },
          organizationId,
        },
      });
    }
  }, [organizationId]);

  return {
    deleteInvitations: deleteInvitationsHandler,
    failureList,
    invitationsFormStatus,
    invitedMembers: listInvitationsResponse.data?.listInvitations || [],
    inviteMembersOpen,
    loadingInvitedMembers: listInvitationsResponse.loading,
    onCloseInviteMembers,
    resendInvitations: resendInvitationsHandler,
    sendInvitations: sendInvitationsHandler,
    setFailureList,
    setInvitationsFormStatus,
    setInviteMembersOpen,
    setShowMultipleFeedback,
    showMultipleFeedback,
    setSuccessList,
    successList,
  };
}
